import * as React from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { HTMLAttributeAnchorTarget } from 'react';
import { Spinner } from 'baseui/spinner';

export type ButtonProps = {
  id?: string;
  type?: 'submit' | 'reset' | 'button';
  disabled?: boolean;
  className?: string;
  component?: React.ElementType;
  loading?: boolean;
  href?: string;
  target?: HTMLAttributeAnchorTarget | undefined;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
} & VariantProps<typeof buttonStyles>;

const buttonStyles = cva(
  twMerge(
    'font-button inline-flex flex-row justify-center items-center',
    'text-white font-normal',
    'border-transparent border border-solid focus:border-borderDark-default',
    'box-border',
    'rounded-main'
  ),
  {
    variants: {
      size: {
        medium: 'text-sm px-4 py-1.5',
        small: 'text-xs px-3 py-1 lg:py-1.5',
      },
      style: {
        primary: 'bg-lightPurple-100',
        text: 'px-0 py-0 bg-transparent text-white border-none active:bg-transparent active:text-white-400',
        neutral:
          'bg-white text-black active:bg-black-500 hover:text-white hover:bg-black-1000 hover:border-black-600',
        accent: 'bg-lightPurple-300',
        outline: 'border-[0.4px] border-black-600 bg-backgroundDark-primary',
        ghost: 'text-white-600',
        'neutral-dark': 'bg-backgroundDark-secondary',
        'outline-dark': 'bg-backgroundDark-secondary border-lightPurple-200',
        square:
          'rounded-none border border-dashed border-[#343434] bg-[#1B1D1E]',
      },
      disabled: {
        true: 'pointer-events-none cursor-not-allowed bg-white-700 active:bg-white-700 focus:bg-white-700 focus:border-transparent',
        false: '',
      },
    },
    defaultVariants: {
      size: 'medium',
      style: 'neutral',
    },
  }
);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function (props, ref) {
    const {
      id,
      disabled = false,
      size = 'medium',
      style,
      children,
      className,
      loading,
      component: Component = 'button',
      href,
      target,
      type = 'button',
      onClick = () => {},
    } = props;

    const classNames = twMerge(
      buttonStyles({ size, style, disabled }),
      className
    );

    const handleClick = disabled ? () => {} : onClick;

    return (
      <Component
        ref={ref}
        id={id}
        href={href}
        target={target}
        type={type}
        className={classNames}
        onClick={handleClick}
      >
        {children}
        {loading && (
          <Spinner className='ml-1' $size='14px' $borderWidth='3px' />
        )}
      </Component>
    );
  }
);
